import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { FilterUid } from 'src/components/organisms/Filter/constants/uids';
import type { ChangedFilterDetails } from 'src/components/organisms/Filter/types';
import type { SortOptionValue } from 'src/components/organisms/FilterMenu/types';
import { healthCheckProducts } from 'src/redux/products/thunks/healthCheckProducts';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import type { RequestProductsActionPayload } from 'src/redux/products/types';
import {
  setOptionActiveByValue,
  setOptionsValues,
  setOptionToActiveRemoveOthers,
  setOptionToActiveSetOthersToInactive,
} from './helpers/filterOptionHelpers';
import type { Filter } from './types/Filter';
import type { SortOption } from './types/SortOption';

export interface CanonicalFilters {
  categoryIDs?: number[];
  partnerShortcode?: string;
  searchTerm?: string;
}

interface LastUpdatedFilter {
  filterOptionActive: boolean;
  filterOptionValue: string | number | null;
  filterUid: string;
}

export interface ProductsQueryState {
  canonicalFilters: CanonicalFilters;
  filters: Filter[];
  isModalOpen: boolean;
  lastUpdatedFilter: LastUpdatedFilter | null;
  page: number;
  sortOptions: SortOption[];
}

export const initialState: Readonly<ProductsQueryState> = {
  canonicalFilters: {},
  filters: [],
  isModalOpen: false,
  lastUpdatedFilter: null,
  page: 1,
  sortOptions: [],
};

export const { actions, reducer } = createSlice({
  name: 'productsQuery',
  initialState,
  reducers: {
    closeModal: (state) => {
      state.isModalOpen = false;
    },
    openModal: (state) => {
      state.isModalOpen = true;
    },
    changeFilterOption: (state, action: PayloadAction<ChangedFilterDetails>) => {
      state.page = 1;
      const changedFilter = action.payload;

      state.filters = state.filters.map((filter) => {
        if (filter.uid !== changedFilter.uid) {
          return filter;
        }

        if (filter.uid === FilterUid.Price) {
          const changedOptionUidValuePairs = changedFilter.options.map((changedOption) => ({
            uid: changedOption.uid!,
            value: changedOption.value,
          }));

          return {
            ...filter,
            options: setOptionsValues(filter.options, changedOptionUidValuePairs),
          };
        }

        const { active, value } = changedFilter.options[0];

        switch (filter.type) {
          case 'tree':
            return {
              ...filter,
              options: setOptionToActiveRemoveOthers(filter.options, value),
            };
          case 'radio':
            return {
              ...filter,
              options: setOptionToActiveSetOthersToInactive(filter.options, value),
            };
          case 'checkbox':
            return {
              ...filter,
              options: setOptionActiveByValue(filter.options, value, active),
            };
          default:
            return filter;
        }
      });

      if (changedFilter.uid !== FilterUid.Sort && changedFilter.uid !== FilterUid.Price) {
        state.lastUpdatedFilter = {
          filterOptionActive: changedFilter.options[0].active,
          filterOptionValue: changedFilter.options[0].value,
          filterUid: changedFilter.uid,
        };
      } else {
        state.lastUpdatedFilter = null;
      }
    },
    resetFilters: (state) => {
      state.page = 1;
      state.filters = state.filters.map((filter) => {
        const hasActiveOption = filter.options.some((option) => option.active);

        if (hasActiveOption) {
          return {
            ...filter,
            options: filter.options.map((option) => {
              if (option.active) {
                return {
                  ...option,
                  value: filter.type === 'range' ? null : option.value,
                  active: false,
                };
              }

              return option;
            }),
          };
        }

        return filter;
      });

      state.lastUpdatedFilter = null;
    },
    changeSortOption: (state, action: PayloadAction<SortOptionValue>) => {
      state.page = 1;
      state.sortOptions = state.sortOptions.map((option) => {
        return {
          ...option,
          active: option.id === action.payload,
        };
      });
    },
    resetSortOptions: (state: ProductsQueryState) => {
      state.page = 1;
      state.sortOptions = state.sortOptions.map((option) => {
        return {
          ...option,
          active: false,
        };
      });
    },
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action): action is PayloadAction<RequestProductsActionPayload> =>
        action.type === healthCheckProducts.fulfilled.type ||
        action.type === requestProducts.fulfilled.type,
      (state, { payload }) => {
        if (payload.sortOptions) {
          state.sortOptions = payload.sortOptions;
        }

        if (payload.filters) {
          state.filters = payload.filters;
        }
      },
    );
  },
});
